import React, { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';
import { throttle } from 'lodash';
import './index.css';

interface Props {
    title: string,
    value: number,
    overValue?: string,
    steps: { percent: number, status: "success" | "warning" | "danger" | "info" }[],
    size?: "tiny" | "small" | "medium-small" | "medium" | "big",
    insideRadius?: string
}

const ChartGauge: React.FC<Props> = (props) => {
    const getColor = (color: string): string => {
        const colorMapping: { [key: string]: string } = {
            "success": "#A5BF3E",
            "warning": "#F3C944",
            "danger": "#F87C8D",
            "info": "#59BAC5",
        };
    
        return colorMapping[color] || "#ccccc";
    };
    
    var option: echarts.EChartsOption = {
        series: [
            {
                type: 'pie',
                radius: [props.insideRadius || '80%', '100%'],
                avoidLabelOverlap: false,
                startAngle: 180,
                silent: true,
                itemStyle: {
                    borderRadius: 4,
                    borderColor: '#fff',
                    borderWidth: 4
                },

                label: {
                    show: false,
                    position: 'center'
                },
                labelLine: {
                    show: false
                },
                data: [
                    ...props.steps.map(s => {
                        return {
                            value: s.percent, itemStyle: {
                                color: getColor(s.status)
                            }
                        }
                    }),
                    {
                        value: props.steps.reduce((t, s) => { return t + s.percent }, 0),
                        itemStyle: {
                            borderColor: '#ffffff00',
                            color: '#ffffff00'
                        }
                    }
                ]
            }
        ]
    }

    const chartDom = useRef(null);

    useEffect(() => {
        if (chartDom && chartDom.current) {
            const myChart = echarts.init(chartDom.current as any, undefined, { renderer: 'svg' });
            myChart.setOption(option);

            const resizeObserver = new ResizeObserver(throttle(() => {
                myChart.resize();
            }, 5000));

            resizeObserver.observe(chartDom.current);

            return () => {
                if (chartDom.current) {
                    resizeObserver.unobserve(chartDom.current);
                }
            };
        }
    }, [chartDom, option]);

    return <div className={`hhchart gauge ${props.size || "medium"}`} >
        <div className="chart" >
            <div className="body" ref={chartDom as any} >
            </div>
            <img
                className='needle'
                style={{ transform: `rotate(${props.value ? 180 / 100 * props.value : 0}deg)` }}
                src="data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiIHN0YW5kYWxvbmU9Im5vIj8+CjxzdmcKICAgd2lkdGg9IjEwMCIKICAgaGVpZ2h0PSIxMDAiCiAgIHZpZXdCb3g9IjAgMCAxMDAgMTAwIgogICBmaWxsPSJub25lIgogICB2ZXJzaW9uPSIxLjEiCiAgIGlkPSJzdmc2IgogICB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciCiAgIHhtbG5zOnN2Zz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgogIDxkZWZzCiAgICAgaWQ9ImRlZnMxMCIgLz4KICA8cGF0aAogICAgIGlkPSJlbGxpcHNlMiIKICAgICBkPSJtIDUwLjQ4OTEzMSw1NS45OTk5OTkgYSA1LjQ4MDg3MTgsNS45OTk5OTk5IDAgMCAwIDUuNDc2OTMxLC02IDUuNDgwODcxOCw1Ljk5OTk5OTkgMCAwIDAgLTUuNDc2OTMxLC02IDUuNDgwODcxOCw1Ljk5OTk5OTkgMCAwIDAgLTAuMDA0LDAgNS40ODA4NzE4LDUuOTk5OTk5OSAwIDAgMCAtMC44MDUzMTUsMC4wNjY0MSBsIC00Ny42MDg0ODMyLDMuOTMzNTkgYyAtMS4wMDkwMjIsMCAtMS44MjYzMDkwNCwwLjg5NTQzMSAtMS44MjYzMDkwNCwyIDAsMS4xMDQ1NjkgMC44MTcyODcwNCwxLjk5OTk5OSAxLjgyNjMwOTA0LDEuOTk5OTk5IGwgNDcuNTk2NjU0MiwzLjkzMTY0MSBhIDUuNDgwODcxOCw1Ljk5OTk5OTkgMCAwIDAgMC44MTcxODcsMC4wNjgzNiBoIDAuMDAxOSBjIDYuNTZlLTQsMCAwLjAwMTMsNGUtNiAwLjAwMiwwIHoiCiAgICAgc3R5bGU9ImZpbGw6Izc3Nzc3NztzdHJva2Utd2lkdGg6MS4wMDY1MTtmaWxsLW9wYWNpdHk6MSIgLz4KPC9zdmc+Cg=="
                alt="needle"
            />
        </div>
        <span className="number">
            <span style={{ fontSize: props.overValue ? "60%" : "100%" }}>{props.overValue || props.value.toFixed(0) + "%"}</span>
        </span>
        <div className="title">{props.title}</div>
    </div>;
}

export default ChartGauge;